import { CloseOutlined } from '@ant-design/icons';
import { Checkbox, Col, Modal, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import { getAccessibleBranchForUser } from 'Api/masters/branch-code';
import { reportApis } from 'Api/reports';
import Buttons from 'components/Global/Buttons';
import GlobalPayPeriod from 'components/Global/PayPeriod';
import SelectDropdown from 'components/Global/SelectDropdown';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConstantDropdownAction } from '../../../../redux/actions/constantDropdownAction';
import { AppDispatch } from 'redux/store';
import { invalidText, toastText } from 'utils/utils';
import styles from './index.module.scss';
import './index.scss';

type GenerateJournalEntryModalProps = {
	open: boolean;
	cancelGenerateModal: () => void;
	fetchJournalEntryDetails: () => void;
};

interface DropdownOption {
	label: string;
	value: string;
}

const GenerateJournalEntryModal = (props: GenerateJournalEntryModalProps) => {
	const { open, cancelGenerateModal, fetchJournalEntryDetails } = props;
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch<AppDispatch>();

	const [payPeriodError, setPayPeriodError] = useState<any>(false);

	const [selectedPayPeriod, setSelectedPayPeriod] = useState<string>('');
	const [selectedYear, setSelectedYear] = useState<any>(dayjs());
	const [payPeriodIds, setPayPeriodIds] = useState<any[]>([]);
	const [payPeriodLoading, setPayPeriodLoading] = useState(false);

	const [currencyType, setCurrencyType] = useState<string>('ZMW');
	const [branchIds, setBranchIds] = useState<string[]>([]);
	const [departmentIds, setDepartmentIds] = useState<string[]>([]);
	const [accessibleBranches, setAccessibleBranches] = useState<
		DropdownOption[]
	>([]);
	const [isBranchesLoading, setIsBranchesLoading] = useState<boolean>(false);

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data
	);

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: loading,
			onclick: () => {
				handleCancelModal();
			},
			minWidth: '10rem',
		},
		{
			text: `${
				payPeriodIds.find(
					(item) => item.payPeriodId === selectedPayPeriod
				)
					? 'Re-Generate'
					: 'Generate'
			}`,
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: payPeriodLoading,
			onclick: handleSubmit,
			minWidth: '10rem',
		},
	];

	const handleCancelModal = () => {
		setSelectedPayPeriod('');
		setSelectedYear(dayjs());
		setPayPeriodError(false);
		setCurrencyType('ZMW');
		setBranchIds([]);
		setDepartmentIds([]);
		cancelGenerateModal();
	};

	async function handleSubmit() {
		if (invalidText(selectedPayPeriod)) {
			setPayPeriodError(true);
			return;
		}
		if (!currencyType) {
			toastText('Please select a currency type', 'error');
			return;
		}

		try {
			setLoading(true);
			const data = {
				payPeriodId: selectedPayPeriod,
				branchIds,
				departmentIds,
				currencyType,
			};
			await reportApis.generateJournalEntryReport(data);
			toastText('Journal Entry report Generated Successfully', 'success');
			fetchJournalEntryDetails();
			handleCancelModal();
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message ||
				error?.response?.data?.error?.errorDescription ||
				'Something went wrong generating Journal Entry report';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	}

	const handleSelectYear = (value: any) => {
		setSelectedPayPeriod('');
		setSelectedYear(value);
	};

	const handlePayPeriodChange = (value: string) => {
		if (invalidText(value)) {
			setPayPeriodError(true);
			setSelectedPayPeriod(value);
			return;
		}

		setSelectedPayPeriod(value);
		setPayPeriodError(false);
	};

	const getPayPeriodDetailsForReport = async () => {
		try {
			setPayPeriodLoading(true);

			const year = new Date(selectedYear).getFullYear();

			const response = await reportApis.getPayPeriodJEReports(
				String(year)
			);
			setPayPeriodIds(response.data.data);
		} catch (error: any) {
			toastText(
				'Something went wrong fetching pay period details',
				'error'
			);
		} finally {
			setPayPeriodLoading(false);
		}
	};

	const fetchAccessibleBranches = async () => {
		try {
			setIsBranchesLoading(true);
			const response = await getAccessibleBranchForUser();
			const branches: DropdownOption[] = response?.data?.data ?? [];
			branches.shift();
			setAccessibleBranches(branches);
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Failed to fetch accessible branches';
			toastText(message, 'error');
		} finally {
			setIsBranchesLoading(false);
		}
	};

	useEffect(() => {
		fetchAccessibleBranches();
		dispatch(fetchConstantDropdownAction({}));
	}, []);

	useEffect(() => {
		if (open) {
			setSelectedPayPeriod('');
			setSelectedYear(dayjs());
		}
	}, [open]);

	useEffect(() => {
		if (selectedYear) {
			getPayPeriodDetailsForReport();
		}
	}, [selectedYear]);

	return (
		<Modal
			open={open}
			width={500}
			maskClosable={false}
			closable={false}
			onCancel={handleCancelModal}
			footer={null}
			style={{ position: 'absolute', top: 200, right: 50 }}
		>
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<Title level={4}>Journal Entry</Title>
					<div
						className={styles['close-icon']}
						onClick={handleCancelModal}
					>
						<CloseOutlined />
					</div>
				</div>
				<div>
					<Row className={styles['form-container-card']} gutter={50}>
						<GlobalPayPeriod
							handlePayPeriod={(value) =>
								handlePayPeriodChange(value)
							}
							selectedPayPeriod={selectedPayPeriod}
							isRow={true}
							handleSelectedYear={(value) =>
								handleSelectYear(value)
							}
							colSpan={24}
							yearError={false}
							isYearRequired={true}
							isPayPeriodRequired={true}
							colStyle={{ marginBottom: '1rem' }}
							payPeriodError={payPeriodError}
							selectedYear={selectedYear}
							rowStyle={{ margin: '0 0 1rem 0' }}
						/>
						<Col span={24} className="margin-top-10">
							<SelectDropdown
								defaultOption={true}
								defaultLabel="Select Branch"
								placeholder="Select Branch"
								mode="multiple"
								options={accessibleBranches}
								value={branchIds}
								onChange={(value) => {
									const newBranchIds = value as string[];
									setBranchIds(newBranchIds);
									const validDepartments =
										dropdownOptions?.department
											?.filter((item: any) =>
												newBranchIds.includes(
													item.branchId
												)
											)
											.map((item: any) => item.value) || [];
									setDepartmentIds((prev) =>
										prev.filter((id) =>
											validDepartments.includes(id)
										)
									);
								}}
								size="large"
								required={false}
								helperText="Branch is required"
								label="Branch"
								isError={false}
								loading={isBranchesLoading}
								disabled={isBranchesLoading}
							/>
						</Col>
						<Col span={24} className="margin-top-10">
							<SelectDropdown
								defaultOption={true}
								defaultLabel="Select Department"
								placeholder="Select Department"
								mode="multiple"
								options={
									branchIds
										? dropdownOptions?.department?.filter(
												(item: any) =>
													branchIds.includes(
														item.branchId
													)
										  )
										: []
								}
								value={departmentIds}
								onChange={(value) => {
									setDepartmentIds(value as string[]);
								}}
								size="large"
								required={false}
								helperText="Department is required"
								label="Department"
								disabled={branchIds.length === 0}
								isError={false}
							/>
						</Col>
					</Row>
					<p className={styles['title']}>Select Currency Type</p>
					<Row className={styles['currency-selector']}>
						<Col>
							<Row>
								<Checkbox
									checked={currencyType === 'ZMW'}
									onChange={() => setCurrencyType('ZMW')}
								>
									ZMW
								</Checkbox>
							</Row>
							<Row>
								<Checkbox
									className={styles['currency-checkbox']}
									checked={currencyType === 'USD'}
									onChange={() => setCurrencyType('USD')}
								>
									USD
								</Checkbox>
							</Row>
						</Col>
					</Row>
					<Row className={styles['modal-buttons']}>
						<Buttons buttons={buttons} />
					</Row>
				</div>
			</div>
		</Modal>
	);
};

export default GenerateJournalEntryModal;
